import { useCallback, useEffect, useMemo, useState } from "react";
import { getTireSizeApi, TireSizeRequest } from "../api/carModelApi";
import { ICarTireSizeItem } from "../types";

/**
 * Custom hook to fetch and manage a list of tire sizes based on the provided tire size request.
 * @param {TireSizeRequest} tireSize - The request object containing car brand and model information.
 * @returns {object} - An object containing:
 *   - `tireSizeList` {ICarTireSizeItem[]} - The list of tire sizes.
 *   - `loading` {boolean} - The loading state indicating if the data is being fetched.
 *   - `error` {boolean} - Indicates if there was an error during fetching.
 *   - `refetch` {() => Promise<void>} - Function to manually refetch the data.
 */
export default function useTireSize(tireSize: TireSizeRequest) {
  const [tireSizeList, setTireSizeList] = useState<ICarTireSizeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  /**
   * Fetches the list of tire sizes from the API and updates the state.
   */
  const getCarModelList = useCallback(async () => {
    if (!tireSize.car_brand || tireSize.car_model === -1) return;

    setLoading(true);
    setError(false);

    try {
      const data = await getTireSizeApi(tireSize);

      if (!data || !data.list || data.list.length === 0) {
        setTireSizeList([]);
        return;
      }

      setTireSizeList(data.list);
    } catch (err) {
      setError(true);
      setTireSizeList([]);
    } finally {
      setLoading(false);
    }
  }, [tireSize.car_brand, tireSize.car_model]);

  /**
   * Effect hook to trigger the API call when the tire size request changes.
   * Ensures the request contains valid car brand and model information.
   */
  useEffect(() => {
    if (tireSize.car_brand && tireSize.car_model !== -1) {
      getCarModelList();
    } else {
      setTireSizeList([]);
    }
  }, [tireSize, getCarModelList]);

  // Provide memoized result to prevent unnecessary re-renders
  return useMemo(
    () => ({
      tireSizeList,
      loading,
      error,
      refetch: getCarModelList,
    }),
    [tireSizeList, loading, error, getCarModelList],
  );
}
